import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Button,
  Modal,
  Tabs,
  Typography,
  Divider,
  DatePicker,
  Tooltip,
  Select,
  App,
} from "antd";
import {
  SyncOutlined,
  SaveOutlined,
  SplitCellsOutlined,
  MergeOutlined,
} from "@ant-design/icons";
import { useSchoolCalendar } from "../../../../hooks/useSchoolCalendar";
import { usePrograms } from "../../../../hooks/usePrograms";
import {
  CalendarEntity,
  EducationLevel,
  UpdateMultipleWeeksRequest,
} from "../../../../services/calendar/calendar.types";
import ActivityIndicator from "../../../ActivityIndicator";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import styles from "../../../../pages/App/Calendar/styles.module.css";
import {
  parseUTCDate,
  toUTCDate,
  toUTCISOString,
  getOrderErrorWeeks,
  formatDate,
  getOverlappingWeeksAll,
  detectOverlapsAll,
} from "../../../../utils/calendarUtils";

dayjs.extend(utc);

const { Title, Text } = Typography;

interface ClassroomSelected {
  id: number;
  name: string;
  // Pre-collapse, each classroom row already scopes to a single program.
  // Siblings sharing (grade_id, custom_id) represent the other materias of
  // the same group — used to derive the materia selector below.
  program_id: number;
  custom_id?: string;
  grade_id: number;
}

// Sentinel Select value for "Calendario del grupo" (the default option) —
// real program ids are always positive, so 0 never collides with one.
const GROUP_BASE_PROGRAM_ID = 0;

type LevelTab = {
  key: string;
  level: EducationLevel;
  label: string;
};

// Same labels and order as the general calendar page (Preescolar first).
const LEVEL_TABS: LevelTab[] = [
  { key: "preescolar", level: "KINDER", label: "Preescolar" },
  { key: "primaria", level: "PRIMARIA", label: "Primaria" },
];

interface CalendarTabProps {
  schoolId: number;
  schoolName: string;
  schoolClassrooms: ClassroomSelected[];
  // Section flags of the school entity; undefined means the backend response
  // predates them.
  mostrarPreescolar?: boolean;
  mostrarPrimaria?: boolean;
}

const CalendarTab: React.FC<CalendarTabProps> = ({
  schoolId,
  schoolName,
  schoolClassrooms,
  mostrarPreescolar,
  mostrarPrimaria,
}) => {
  const { notification } = App.useApp();
  // Estilos comunes para los modales
  const modalSuccessStyle = {
    padding: 12,
    background: "var(--bo-success-bg)",
    borderRadius: 6,
    border: "1px solid var(--bo-success-border)",
  };

  const modalWarningStyle = {
    padding: 12,
    background: "var(--bo-warn-bg)",
    borderRadius: 6,
    border: "1px solid var(--bo-warn-border)",
  };

  const modalTextMargin = { marginBottom: 16 };

  const {
    calendarsByLevel,
    loading,
    hasPersonalizedCalendarByLevel,
    isDividedByClassrooms: hookIsDividedByClassrooms,
    classroomCalendars,
    fetchSchoolCalendar,
    initSchoolCalendar,
    syncWithGeneralCalendar,
    syncClassroomWithGeneral,
    syncClassroomWithSchool,
    updateSchoolWeekDate,
    divideByClassrooms,
    unifyCalendars,
    fetchClassroomCalendar,
    createClassroomCalendarForClass,
    updateClassroomWeekDate,
    programOverrideSource,
    programOverrideCalendar,
    programOverrideLoading,
    fetchResolvedProgramCalendar,
    createProgramOverride,
    removeProgramOverride,
    updateProgramWeekDate,
    clearProgramOverrideState,
  } = useSchoolCalendar(schoolId);

  const { programs } = usePrograms();

  // Only the sections the school has enabled get a tab. When both flags are
  // absent (old backend response) fall back to both levels, so the tab keeps
  // working instead of rendering nothing.
  const levelTabs = useMemo(() => {
    const enabled = LEVEL_TABS.filter((tab) =>
      tab.level === "KINDER" ? mostrarPreescolar : mostrarPrimaria
    );
    return enabled.length > 0 ? enabled : LEVEL_TABS;
  }, [mostrarPreescolar, mostrarPrimaria]);

  // Active level lives in component state (this tab sits inside the school
  // form's own tab system, so the URL param the general page uses is taken).
  const [activeLevel, setActiveLevel] = useState<EducationLevel>(
    levelTabs[0].level
  );

  // Keep the active level valid if the school's sections change after mount.
  useEffect(() => {
    if (!levelTabs.some((tab) => tab.level === activeLevel)) {
      setActiveLevel(levelTabs[0].level);
    }
  }, [levelTabs, activeLevel]);

  const activeLevelTab =
    levelTabs.find((tab) => tab.level === activeLevel) ?? levelTabs[0];
  const calendar = useMemo(
    () => calendarsByLevel[activeLevel] ?? [],
    [calendarsByLevel, activeLevel]
  );
  const hasPersonalizedCalendar = hasPersonalizedCalendarByLevel[activeLevel];

  const [isInitModalVisible, setIsInitModalVisible] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);

  const [editingWeek, setEditingWeek] = useState<number | null>(null);
  const [editingDate, setEditingDate] = useState<dayjs.Dayjs | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [previewCalendar, setPreviewCalendar] = useState<CalendarEntity[]>([]);
  const [showPreview, setShowPreview] = useState(false);

  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [pendingDateChange, setPendingDateChange] = useState<{
    week: number;
    date: dayjs.Dayjs;
  } | null>(null);

  // Estados para división por clases
  const [isDividedByClassrooms, setIsDividedByClassrooms] = useState(false);
  const [activeClassroom, setActiveClassroom] = useState<number | null>(null);
  const [showUnifyModal, setShowUnifyModal] = useState(false);

  // Estados para confirmación de cambio de clase
  const [showChangeClassroomModal, setShowChangeClassroomModal] =
    useState(false);
  const [pendingClassroomChange, setPendingClassroomChange] = useState<
    number | null
  >(null);

  // Estados para confirmación de cambio de sección (Preescolar/Primaria) con
  // cambios pendientes — same guard as the classroom change: pending edits
  // are never dropped silently nor carried into the other level.
  const [showChangeLevelModal, setShowChangeLevelModal] = useState(false);
  const [pendingLevelChange, setPendingLevelChange] =
    useState<EducationLevel | null>(null);

  // Estados para el calendario por materia (override), classroom-group-model
  const [selectedProgramId, setSelectedProgramId] = useState<number | null>(
    null
  );
  const [showRevertOverrideModal, setShowRevertOverrideModal] =
    useState(false);

  const isProgramOverrideActive =
    selectedProgramId !== null && programOverrideSource === "PROGRAM_OVERRIDE";

  // Una clase sin calendario propio hereda el de la escuela. El GET resuelve por
  // jerarquia (CLASSROOM > SCHOOL > GENERAL) y siempre devuelve semanas, asi que
  // la presencia no alcanza: las heredadas vienen con classroom_id null. Sin
  // esta distincion la UI mostraba el calendario de la escuela como si fuera de
  // la clase y dejaba habilitadas acciones que el backend rechaza con
  // "Classroom X does not have a custom calendar in school Y".
  const activeClassroomHasOwnCalendar = Boolean(
    activeClassroom &&
      classroomCalendars[activeClassroom]?.some(
        (week) => week.classroom_id === activeClassroom
      )
  );
  const showClassWithoutCalendar =
    isDividedByClassrooms &&
    Boolean(activeClassroom) &&
    !activeClassroomHasOwnCalendar &&
    selectedProgramId === null;

  // Memoized current calendar data - considera si está dividido por clases
  const currentCalendarData = useMemo(() => {
    if (isProgramOverrideActive) {
      return showPreview && previewCalendar.length > 0
        ? previewCalendar
        : programOverrideCalendar;
    }

    if (
      isDividedByClassrooms &&
      activeClassroom &&
      classroomCalendars[activeClassroom]
    ) {
      return showPreview && previewCalendar.length > 0
        ? previewCalendar
        : classroomCalendars[activeClassroom];
    }

    return showPreview && previewCalendar.length > 0
      ? previewCalendar
      : calendar;
  }, [
    showPreview,
    previewCalendar,
    calendar,
    isDividedByClassrooms,
    activeClassroom,
    classroomCalendars,
    isProgramOverrideActive,
    programOverrideCalendar,
  ]);

  // Memoized calculations for overlapping and order error weeks
  const overlappingWeeks = useMemo(() => {
    return getOverlappingWeeksAll(currentCalendarData);
  }, [currentCalendarData]);

  const orderErrorWeeks = useMemo(() => {
    return getOrderErrorWeeks(currentCalendarData);
  }, [currentCalendarData]);

  const hasErrors = useMemo(() => {
    return overlappingWeeks.length > 0 || orderErrorWeeks.length > 0;
  }, [overlappingWeeks, orderErrorWeeks]);

  // Función auxiliar para verificar si una semana fue modificada
  const isWeekChanged = useCallback(
    (week: CalendarEntity): boolean => {
      if (!showPreview || previewCalendar.length === 0) return false;

      const originalCalendar = isProgramOverrideActive
        ? programOverrideCalendar
        : isDividedByClassrooms &&
          activeClassroom &&
          classroomCalendars[activeClassroom]
        ? classroomCalendars[activeClassroom]
        : calendar;

      const previewWeek = previewCalendar.find((pw) => pw.week === week.week);
      const originalWeek = originalCalendar.find((cw) => cw.week === week.week);

      return !!(
        previewWeek &&
        originalWeek &&
        previewWeek.start_date !== originalWeek.start_date
      );
    },
    [
      showPreview,
      previewCalendar,
      calendar,
      isDividedByClassrooms,
      activeClassroom,
      classroomCalendars,
      isProgramOverrideActive,
      programOverrideCalendar,
    ]
  );

  // La API devuelve una fila VIRTUAL por materia de cada grupo: mismo id y
  // mismo nombre, solo cambia program_id. Estas filas alimentan el selector de
  // materias de más abajo.
  const classroomProgramRows = useMemo(() => {
    return schoolClassrooms || [];
  }, [schoolClassrooms]);

  // El selector de CLASE lista un grupo una sola vez — sin esto, un grupo con 4
  // materias aparecía 4 veces (y con 4 keys de React repetidas).
  const availableClassrooms = useMemo(() => {
    const byClassroomId = new Map<number, (typeof classroomProgramRows)[number]>();
    classroomProgramRows.forEach((row) => {
      if (!byClassroomId.has(row.id)) {
        byClassroomId.set(row.id, row);
      }
    });
    return Array.from(byClassroomId.values());
  }, [classroomProgramRows]);

  // Materia activa (dentro del clase seleccionado) para derivar las opciones
  // del selector de materias — agrupadas por (grade_id, custom_id), igual
  // que ClassesTab/GenerarClasesModal agrupan clases hermanas del mismo grupo.
  const activeClassroomInfo = useMemo(
    () => availableClassrooms.find((c) => c.id === activeClassroom),
    [availableClassrooms, activeClassroom]
  );

  const programOptionsForActiveClassroom = useMemo(() => {
    if (!activeClassroomInfo) return [];

    const siblings = classroomProgramRows.filter(
      (c) =>
        c.custom_id !== undefined &&
        c.custom_id === activeClassroomInfo.custom_id &&
        c.grade_id === activeClassroomInfo.grade_id
    );

    const uniqueByProgram = new Map<number, ClassroomSelected>();
    siblings.forEach((sibling) => {
      if (!uniqueByProgram.has(sibling.program_id)) {
        uniqueByProgram.set(sibling.program_id, sibling);
      }
    });

    return Array.from(uniqueByProgram.values()).map((sibling) => ({
      program_id: sibling.program_id,
      title:
        programs.find((p) => p.id === sibling.program_id)?.title ||
        `Programa ${sibling.program_id}`,
    }));
  }, [classroomProgramRows, activeClassroomInfo, programs]);

  // Inicializar activeClassroom con la primera clase disponible cuando se divide
  useEffect(() => {
    if (
      isDividedByClassrooms &&
      !activeClassroom &&
      availableClassrooms.length > 0
    ) {
      setActiveClassroom(availableClassrooms[0].id);
    }
  }, [isDividedByClassrooms, activeClassroom, availableClassrooms]);

  // Cargar calendario de la clase activa cuando cambia
  useEffect(() => {
    if (
      isDividedByClassrooms &&
      activeClassroom &&
      !classroomCalendars[activeClassroom]
    ) {
      fetchClassroomCalendar(activeClassroom);
    }
  }, [
    isDividedByClassrooms,
    activeClassroom,
    classroomCalendars,
    fetchClassroomCalendar,
  ]);

  // Only the level being shown is fetched, and only while it is unfetched
  // (`null`) — mirrors the general calendar page.
  useEffect(() => {
    if (schoolId && calendarsByLevel[activeLevel] === null) {
      fetchSchoolCalendar(activeLevel, availableClassrooms);
    }
  }, [
    schoolId,
    activeLevel,
    calendarsByLevel,
    fetchSchoolCalendar,
    availableClassrooms,
  ]);

  // Sincronizar estado de división del hook con el estado local
  useEffect(() => {
    setIsDividedByClassrooms(hookIsDividedByClassrooms);
  }, [hookIsDividedByClassrooms]);

  const handleInitialize = async () => {
    try {
      await initSchoolCalendar(activeLevel);
      setIsInitModalVisible(false);
    } catch (error) {
      notification.error({
        message: "Error",
        description:
          "Error al crear el calendario personalizado de la escuela. Por favor, inténtalo de nuevo.",
        placement: "top",
        duration: 5,
      });
      console.error("Error initializing school calendar:", error);
    }
  };

  const handleSyncWithGeneral = async () => {
    try {
      if (isDividedByClassrooms && activeClassroom) {
        // Si está dividido por clases, sincronizar solo el clase activa con general
        await syncClassroomWithGeneral(activeClassroom);
      } else {
        // Si no está dividido, sincronizar SOLO el nivel activo de la escuela
        await syncWithGeneralCalendar(activeLevel);
      }
      setShowSyncModal(false);
    } catch (error) {
      notification.error({
        message: "Error",
        description:
          "Error al sincronizar el calendario con el general. Por favor, inténtalo de nuevo.",
        placement: "top",
        duration: 5,
      });
      console.error("Error syncing with general calendar:", error);
    }
  };

  const handleSyncWithSchool = async () => {
    try {
      if (isDividedByClassrooms && activeClassroom) {
        // Sincronizar el clase activa con el calendario de la escuela
        await syncClassroomWithSchool(activeClassroom);
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description:
          "Error al sincronizar el calendario del clase con la escuela. Por favor, inténtalo de nuevo.",
        placement: "top",
        duration: 5,
      });
      console.error("Error syncing classroom with school calendar:", error);
    }
  };

  // ========== FUNCIONES PARA DIVISIÓN POR claseS ==========

  const handleDivideByClassrooms = async () => {
    try {
      const classroomIds = availableClassrooms.map((c) => c.id);
      await divideByClassrooms(classroomIds);
    } catch (error) {
      // El error ya se maneja en el hook
      console.error("Error in handleDivideByClassrooms:", error);
    }
  };

  const handleUnifyCalendars = () => {
    setShowUnifyModal(true);
  };

  const handleConfirmUnify = async () => {
    try {
      const classroomIds = availableClassrooms.map((c) => c.id);
      await unifyCalendars(classroomIds);
      setActiveClassroom(null);
      setShowUnifyModal(false);
    } catch (error) {
      // El error ya se maneja en el hook
      console.error("Error in handleConfirmUnify:", error);
    }
  };

  const handleCancelUnify = () => {
    setShowUnifyModal(false);
  };

  // ========== FUNCIONES PARA CAMBIO DE clase CON CONFIRMACIÓN ==========

  // Función helper para limpiar estados de edición
  const clearEditingStates = useCallback(() => {
    setEditingWeek(null);
    setEditingDate(null);
    setShowPreview(false);
    setPreviewCalendar([]);
  }, []);

  const handleClassroomChange = (newClassroomId: number) => {
    // Si hay cambios pendientes (preview activo), mostrar confirmación
    if (showPreview && previewCalendar.length > 0) {
      setPendingClassroomChange(newClassroomId);
      setShowChangeClassroomModal(true);
    } else {
      // No hay cambios pendientes, cambiar directamente
      changeClassroom(newClassroomId);
    }
  };

  const changeClassroom = (classroomId: number) => {
    clearEditingStates();
    setActiveClassroom(classroomId);
    setSelectedProgramId(null);
    clearProgramOverrideState();
  };

  // ========== FUNCIONES PARA CALENDARIO POR MATERIA (OVERRIDE) ==========

  const handleProgramChange = (rawValue: number) => {
    const programId =
      rawValue === GROUP_BASE_PROGRAM_ID ? null : rawValue;
    clearEditingStates();
    // Clear BEFORE fetching: otherwise the previous materia's override stays
    // active during the request and its weeks render editable under the new
    // selection (save/revert would target the wrong classroom+program pair).
    clearProgramOverrideState();
    setSelectedProgramId(programId);
    if (programId && activeClassroom) {
      fetchResolvedProgramCalendar(activeClassroom, programId);
    }
  };

  const handleCreateOverride = async () => {
    if (!activeClassroom || !selectedProgramId) return;
    try {
      await createProgramOverride(activeClassroom, selectedProgramId);
    } catch (error) {
      // El error ya se maneja en el hook
      console.error("Error creating program override:", error);
    }
  };

  const handleRevertOverride = async () => {
    if (!activeClassroom || !selectedProgramId) return;
    try {
      await removeProgramOverride(activeClassroom, selectedProgramId);
      setShowRevertOverrideModal(false);
    } catch (error) {
      // El error ya se maneja en el hook
      console.error("Error reverting program override:", error);
    }
  };

  const handleConfirmClassroomChange = () => {
    if (pendingClassroomChange !== null) {
      changeClassroom(pendingClassroomChange);
    }
    setShowChangeClassroomModal(false);
    setPendingClassroomChange(null);
  };

  const handleCancelClassroomChange = () => {
    setShowChangeClassroomModal(false);
    setPendingClassroomChange(null);
  };

  // ========== FUNCIONES PARA CAMBIO DE SECCIÓN (NIVEL) ==========

  const changeLevel = (level: EducationLevel) => {
    clearEditingStates();
    setActiveLevel(level);
  };

  const handleLevelTabChange = (key: string) => {
    const tab = levelTabs.find((levelTab) => levelTab.key === key);
    if (!tab || tab.level === activeLevel) return;

    if (showPreview && previewCalendar.length > 0) {
      setPendingLevelChange(tab.level);
      setShowChangeLevelModal(true);
    } else {
      changeLevel(tab.level);
    }
  };

  const handleConfirmLevelChange = () => {
    if (pendingLevelChange !== null) {
      changeLevel(pendingLevelChange);
    }
    setShowChangeLevelModal(false);
    setPendingLevelChange(null);
  };

  const handleCancelLevelChange = () => {
    setShowChangeLevelModal(false);
    setPendingLevelChange(null);
  };

  // ========== FUNCIONES DE EDICIÓN (IGUAL QUE EL CALENDARIO GENERAL) ==========

  const handleDateClick = (week: CalendarEntity) => {
    setEditingWeek(week.week);
    setEditingDate(parseUTCDate(week.start_date));
  };

  const handleDateSave = () => {
    // Solo cerrar la edición, el guardado final se hace con el botón GUARDAR
    setEditingWeek(null);
    setEditingDate(null);
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
  };

  // Función para manejar el click del botón GUARDAR principal
  const handleSaveAll = () => {
    if (showPreview && previewCalendar.length > 0) {
      // Si hay vista previa, mostrar modal de confirmación final
      setShowConfirmModal(true);
    } else {
      notification.info({
        message: "Sin cambios",
        description: "No hay cambios pendientes por guardar.",
        placement: "top",
        duration: 3,
      });
    }
  };

  // Función para ejecutar el guardado final
  const handleFinalConfirm = async () => {
    if (!showPreview || previewCalendar.length === 0) return;

    // VALIDAR SOLAPAMIENTOS antes de guardar
    const hasOverlaps = detectOverlapsAll(previewCalendar);
    if (hasOverlaps) {
      notification.error({
        message: "Error: Fechas solapadas",
        description: `Las semanas ${overlappingWeeks.join(
          ", "
        )} tienen fechas duplicadas. Por favor corrige estas fechas antes de guardar.`,
        placement: "top",
        duration: 8,
      });
      return; // No continuar con el guardado
    }

    // Encontrar qué semanas cambiaron
    const originalCalendar = isProgramOverrideActive
      ? programOverrideCalendar
      : isDividedByClassrooms &&
        activeClassroom &&
        classroomCalendars[activeClassroom]
      ? classroomCalendars[activeClassroom]
      : calendar;

    const changedWeeks = previewCalendar.filter((previewWeek) => {
      const originalWeek = originalCalendar.find(
        (cw) => cw.week === previewWeek.week
      );
      return originalWeek && originalWeek.start_date !== previewWeek.start_date;
    });

    if (changedWeeks.length === 0) return;

    try {
      // Enviar todas las semanas juntas en una sola llamada
      if (changedWeeks.length === 1) {
        // Formato individual para una sola semana
        const updateData = {
          week: changedWeeks[0].week,
          start_date: changedWeeks[0].start_date,
        };

        if (isProgramOverrideActive && activeClassroom && selectedProgramId) {
          await updateProgramWeekDate(activeClassroom, selectedProgramId, updateData);
        } else if (isDividedByClassrooms && activeClassroom) {
          await updateClassroomWeekDate(activeClassroom, updateData);
        } else {
          await updateSchoolWeekDate(activeLevel, updateData);
        }
      } else {
        // Formato masivo para múltiples semanas
        const multipleWeeksData: UpdateMultipleWeeksRequest = {
          weeks: changedWeeks.map((week) => ({
            week: week.week,
            start_date: week.start_date,
          })),
        };

        if (isProgramOverrideActive && activeClassroom && selectedProgramId) {
          await updateProgramWeekDate(
            activeClassroom,
            selectedProgramId,
            multipleWeeksData
          );
        } else if (isDividedByClassrooms && activeClassroom) {
          await updateClassroomWeekDate(activeClassroom, multipleWeeksData);
        } else {
          await updateSchoolWeekDate(activeLevel, multipleWeeksData);
        }
      }

      // Limpiar estados después del guardado exitoso
      setShowPreview(false);
      setPreviewCalendar([]);
      setShowConfirmModal(false);

      notification.success({
        message: "Cambios guardados",
        description:
          "Las fechas del calendario han sido actualizadas correctamente.",
        placement: "top",
        duration: 4,
      });
    } catch (error) {
      notification.error({
        message: "Error al guardar",
        description:
          "Ocurrió un error al intentar guardar los cambios. Por favor, inténtalo de nuevo.",
        placement: "top",
        duration: 5,
      });
    }
  };

  const handleDateCancel = () => {
    clearEditingStates();
  };

  // Función auxiliar para obtener el calendario base y aplicar cambios
  const applyCalendarChanges = (updatedCalendar: CalendarEntity[]) => {
    setPreviewCalendar(updatedCalendar);
    setShowPreview(true);
  };

  const getBaseCalendar = (): CalendarEntity[] => {
    if (showPreview && previewCalendar.length > 0) {
      return previewCalendar;
    }

    if (isProgramOverrideActive) {
      return programOverrideCalendar;
    }

    if (
      isDividedByClassrooms &&
      activeClassroom &&
      classroomCalendars[activeClassroom]
    ) {
      return classroomCalendars[activeClassroom];
    }

    return calendar;
  };

  // Función para aplicar cambio solo a la semana seleccionada (sin ajustar posteriores)
  const applyDateChangeOnly = (targetWeek: number, newDate: dayjs.Dayjs) => {
    const baseCalendar = getBaseCalendar();

    const updatedCalendar = baseCalendar.map((week) =>
      week.week === targetWeek
        ? { ...week, start_date: toUTCISOString(newDate) }
        : week
    );
    applyCalendarChanges(updatedCalendar);
  };

  // Función para aplicar cambio con ajuste automático (+7 días a posteriores)
  const applyDateChangeWithAdjustment = (
    targetWeek: number,
    newDate: dayjs.Dayjs
  ) => {
    const baseCalendar = getBaseCalendar();
    const updatedCalendar = [...baseCalendar];

    // Actualizar la semana objetivo
    const targetWeekIndex = updatedCalendar.findIndex(
      (w) => w.week === targetWeek
    );
    if (targetWeekIndex === -1) return;

    updatedCalendar[targetWeekIndex] = {
      ...updatedCalendar[targetWeekIndex],
      start_date: toUTCISOString(newDate),
    };

    // Actualizar todas las semanas posteriores sumando +7 días
    let currentDate = newDate;
    for (let i = targetWeekIndex + 1; i < updatedCalendar.length; i++) {
      const weekData = updatedCalendar[i];
      if (weekData.week > targetWeek) {
        currentDate = currentDate.add(7, "day");
        updatedCalendar[i] = {
          ...weekData,
          start_date: toUTCISOString(currentDate),
        };
      }
    }

    applyCalendarChanges(updatedCalendar);
  };

  // Manejar respuesta del modal de consulta
  const handleAdjustModalResponse = (shouldAdjust: boolean) => {
    if (pendingDateChange) {
      if (shouldAdjust) {
        applyDateChangeWithAdjustment(
          pendingDateChange.week,
          pendingDateChange.date
        );
      } else {
        applyDateChangeOnly(pendingDateChange.week, pendingDateChange.date);
      }
    }
    setShowAdjustModal(false);
    setPendingDateChange(null);
  };

  // Función para detectar si la nueva fecha rompe la secuencia de +7 días
  const detectSequenceBreak = (
    targetWeek: number,
    newDate: dayjs.Dayjs
  ): boolean => {
    const baseCalendar = getBaseCalendar();
    const subsequentWeeks = baseCalendar
      .filter((w) => w.week > targetWeek)
      .sort((a, b) => a.week - b.week);
    if (subsequentWeeks.length === 0) return false;

    // Verificar si la nueva fecha + 7 días coincide con la próxima semana
    const nextWeek = subsequentWeeks[0];
    const expectedNextDate = newDate.add(7, "day");
    const actualNextDate = parseUTCDate(nextWeek.start_date);

    // Si la fecha esperada no coincide con la actual, hay ruptura de secuencia
    return !expectedNextDate.isSame(actualNextDate, "day");
  };

  // Función para manejar cambios en el DatePicker
  const handleDateChange = (date: dayjs.Dayjs | null) => {
    // Convertir la fecha seleccionada a UTC manteniendo la fecha exacta
    const utcDate = date ? toUTCDate(date) : null;
    setEditingDate(utcDate);

    if (!utcDate || !editingWeek) {
      setShowPreview(false);
      setPreviewCalendar([]);
      return;
    }

    const baseCalendar = getBaseCalendar();
    const affectedWeeks = baseCalendar.filter((w) => w.week > editingWeek);

    if (affectedWeeks.length > 0) {
      // Mostrar modal si la nueva fecha rompe la secuencia de +7 días
      const breaksSequence = detectSequenceBreak(editingWeek, utcDate);

      if (breaksSequence) {
        setPendingDateChange({ week: editingWeek, date: utcDate });
        setShowAdjustModal(true);
      } else {
        // No rompe secuencia, aplicar cambio solo a esta semana
        applyDateChangeOnly(editingWeek, utcDate);
      }
    } else {
      // No hay semanas posteriores, aplicar cambio directamente
      applyDateChangeOnly(editingWeek, utcDate);
    }
  };

  const anyLevelPersonalized =
    hasPersonalizedCalendarByLevel.KINDER ||
    hasPersonalizedCalendarByLevel.PRIMARIA;

  // Última semana del ciclo mostrado, leída de los datos (36 semanas
  // Preescolar / 38 Primaria) en vez de un total fijo.
  const lastWeek = currentCalendarData.reduce(
    (max, week) => Math.max(max, week.week),
    0
  );

  const renderCalendarTable = () => (
    <div className={styles.calendarContainer}>
      <div className={styles.tableHeader}>
        <div className={styles.weekColumn}>
          <Text strong>Semana</Text>
        </div>
        <div className={styles.dateColumn}>
          <Text strong>Fecha inicio</Text>
        </div>
      </div>

      <Divider className={styles.divider} />

      <div className={styles.calendarList}>
        {currentCalendarData.map((week) => {
          const isChanged = isWeekChanged(week);

          // Detectar errores específicos para esta semana
          const hasOverlap = overlappingWeeks.includes(week.week);
          const hasOrderError = orderErrorWeeks.includes(week.week);
          const hasAnyError = hasOverlap || hasOrderError;

          return (
            <div
              key={week.id}
              className={`${styles.calendarRow} ${
                isChanged ? styles.changedRow : ""
              } ${hasAnyError ? styles.errorRow : ""}`}
            >
              <div className={styles.weekNumber}>
                <Text>{week.week}</Text>
                {hasAnyError && (
                  <div
                    className={styles.errorIndicator}
                    title={
                      hasOverlap && hasOrderError
                        ? "Fecha duplicada y orden incorrecto"
                        : hasOverlap
                        ? "Fecha duplicada con otra semana"
                        : "Orden de fecha incorrecto"
                    }
                  >
                    ❌
                  </div>
                )}
              </div>
              <div className={styles.dateContainer}>
                {editingWeek === week.week ? (
                  <div className={styles.editingContainer}>
                    <DatePicker
                      value={editingDate}
                      onChange={handleDateChange}
                      format="DD MMM YY"
                      placeholder="Seleccionar fecha"
                      className={styles.datePicker}
                    />
                    <div className={styles.editingInfo}>
                      <Text type="secondary" style={{ fontSize: "12px" }}>
                        {editingWeek < lastWeek
                          ? `Al cambiar esta fecha, las semanas ${
                              editingWeek + 1
                            }-${lastWeek} se pueden ajustar automáticamente (+7 días) si lo deseas`
                          : "Esta es la última semana del calendario"}
                      </Text>
                    </div>
                    <div className={styles.editButtons}>
                      <Button size="small" onClick={handleDateCancel}>
                        Cancelar
                      </Button>
                      <Button
                        size="small"
                        type="primary"
                        onClick={handleDateSave}
                        disabled={!editingDate}
                      >
                        {showPreview ? "Aplicar cambios" : "Guardar"}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div
                    className={styles.dateDisplay}
                    onClick={() => handleDateClick(week)}
                  >
                    <Text className={styles.dateText}>
                      {formatDate(week.start_date)}
                    </Text>
                    {isChanged && (
                      <Text className={styles.changedIndicator} type="warning">
                        • Modificado
                      </Text>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderSaveButton = () => (
    <Tooltip
      title={
        editingWeek !== null
          ? "Aplica los cambios pendientes antes de guardar"
          : !showPreview
          ? "Modifica alguna fecha del calendario para habilitar el guardado"
          : hasErrors
          ? "Corrige los errores del calendario antes de guardar"
          : "Guardar cambios realizados en el calendario"
      }
    >
      <Button
        type="primary"
        icon={<SaveOutlined />}
        onClick={handleSaveAll}
        disabled={
          editingWeek !== null ||
          !showPreview ||
          hasErrors ||
          showClassWithoutCalendar
        }
      >
        GUARDAR
      </Button>
    </Tooltip>
  );

  // Panel de una sección: cada nivel tiene su propio indicador
  // (Personalizado), su sincronización y su tabla, de forma independiente.
  const renderLevelPanel = (tab: LevelTab) => {
    if (!hasPersonalizedCalendar) {
      return (
        <>
          <div
            style={{
              marginBottom: 16,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              type="primary"
              className={styles.saveButton}
              onClick={() => setIsInitModalVisible(true)}
            >
              Crear Calendario Personalizado
            </Button>
          </div>
          <div className={styles.emptyState}>
            <Text>
              Esta escuela no tiene un calendario personalizado de {tab.label}.
              Está utilizando el calendario general de Emmi como referencia.
            </Text>
            <br />
            <br />
            <Text type="secondary">
              Para personalizar las fechas de {tab.label} específicamente para
              esta escuela, crea un calendario personalizado que copiará
              inicialmente las fechas del calendario general de {tab.label}.
            </Text>
          </div>
        </>
      );
    }

    return (
      <>
        <div
          style={{
            marginBottom: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <div
            style={{
              flex: "0 0 auto",
              display: "flex",
              gap: "8px",
              alignItems: "center",
            }}
          >
            <Button
              type="default"
              icon={<SyncOutlined />}
              onClick={() => setShowSyncModal(true)}
            >
              Sincronizar con Calendario General EMMI
            </Button>
            <span style={{ fontSize: "14px", color: "#52c41a" }}>
              (Personalizado)
            </span>
          </div>
          <div style={{ flex: "0 0 auto" }}>{renderSaveButton()}</div>
        </div>

        {calendar.length === 0 ? (
          <div className={styles.emptyState}>
            <Text>Cargando calendario personalizado de la escuela...</Text>
          </div>
        ) : (
          renderCalendarTable()
        )}
      </>
    );
  };

  if (loading) {
    return <ActivityIndicator />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div style={{ textAlign: "center", flex: 1 }}>
          <Title level={3}>
            Calendario semanal - {schoolName}
            {/* Dividido implica personalizado; en el modo por niveles el
                indicador es por sección y vive dentro de cada pestaña. */}
            {isDividedByClassrooms && (
              <span
                style={{
                  fontSize: "14px",
                  color: "#52c41a",
                  marginLeft: "8px",
                }}
              >
                (Personalizado)
              </span>
            )}
          </Title>
        </div>
      </div>

      {isDividedByClassrooms ? (
        <>
          <div
            style={{
              marginBottom: 16,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <div style={{ flex: "0 0 auto", display: "flex", gap: "8px" }}>
              <Button
                type="default"
                icon={<SyncOutlined />}
                onClick={() => setShowSyncModal(true)}
                disabled={!activeClassroom || !activeClassroomHasOwnCalendar}
              >
                Sincronizar{" "}
                {activeClassroom
                  ? availableClassrooms.find((c) => c.id === activeClassroom)
                      ?.name
                  : "clase"}{" "}
                con Calendario General EMMI
              </Button>
              <Button
                type="default"
                icon={<MergeOutlined />}
                onClick={handleUnifyCalendars}
              >
                Unificar calendarios
              </Button>
            </div>
            <div style={{ flex: "0 0 auto" }}>{renderSaveButton()}</div>
          </div>

          <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
            <Text strong>Clase:</Text>
            <Select
              value={activeClassroom}
              onChange={handleClassroomChange}
              style={{ minWidth: 200 }}
              placeholder="Selecciona un clase"
            >
              {availableClassrooms.map((classroom) => (
                <Select.Option key={classroom.id} value={classroom.id}>
                  {classroom.name}
                </Select.Option>
              ))}
            </Select>
          </div>

          {activeClassroom && (
            <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <Text strong>Asignatura:</Text>
              <Select
                value={selectedProgramId ?? GROUP_BASE_PROGRAM_ID}
                onChange={handleProgramChange}
                style={{ minWidth: 220 }}
                placeholder="Selecciona una asignatura"
              >
                <Select.Option value={GROUP_BASE_PROGRAM_ID}>
                  Calendario del grupo
                </Select.Option>
                {programOptionsForActiveClassroom.map((program) => (
                  <Select.Option key={program.program_id} value={program.program_id}>
                    {program.title}
                  </Select.Option>
                ))}
              </Select>

              {isProgramOverrideActive && (
                <>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Calendario propio de esta asignatura
                  </Text>
                  <Button
                    danger
                    size="small"
                    onClick={() => setShowRevertOverrideModal(true)}
                  >
                    Volver al calendario del grupo
                  </Button>
                </>
              )}
            </div>
          )}

          {showClassWithoutCalendar ? (
            <div className={styles.emptyState}>
              <Text>
                Esta clase todavía no tiene un calendario propio. Está usando el
                calendario de la escuela, así que no se puede sincronizar ni
                editar desde acá.
              </Text>
              <br />
              <br />
              <Button
                type="primary"
                onClick={() =>
                  activeClassroom &&
                  createClassroomCalendarForClass(activeClassroom)
                }
              >
                Crear calendario para esta clase
              </Button>
            </div>
          ) : selectedProgramId !== null && !isProgramOverrideActive ? (
            programOverrideLoading ? (
              <ActivityIndicator />
            ) : (
              <div className={styles.emptyState}>
                <Text>
                  Esta asignatura todavía no tiene un calendario propio. Está
                  utilizando el calendario del grupo
                  {programOverrideSource === "SCHOOL"
                    ? " (heredado del calendario de la escuela)"
                    : programOverrideSource === "GENERAL"
                    ? " (heredado del calendario general de Emmi)"
                    : ""}
                  .
                </Text>
                <br />
                <br />
                <Button type="primary" onClick={handleCreateOverride}>
                  Crear calendario para esta asignatura
                </Button>
              </div>
            )
          ) : (
            renderCalendarTable()
          )}
        </>
      ) : (
        <>
          {/* Los calendarios por clase no distinguen sección (nivel): dividir
              es independiente del nivel activo y por eso vive fuera de las
              pestañas. */}
          {anyLevelPersonalized && (
            <div style={{ marginBottom: 8 }}>
              <Button
                type="default"
                icon={<SplitCellsOutlined />}
                onClick={handleDivideByClassrooms}
                disabled={availableClassrooms.length === 0}
              >
                Dividir calendario por clases
              </Button>
            </div>
          )}
          <Tabs
            activeKey={activeLevelTab.key}
            onChange={handleLevelTabChange}
            items={levelTabs.map((tab) => ({
              key: tab.key,
              label: tab.label,
              // Solo la pestaña activa monta su panel: el estado de edición
              // nunca se comparte entre niveles.
              children:
                tab.level === activeLevel ? renderLevelPanel(tab) : null,
            }))}
            destroyInactiveTabPane
          />
        </>
      )}

      {/* Modal para crear calendario personalizado */}
      <Modal
        title="Crear Calendario Personalizado"
        open={isInitModalVisible}
        onOk={handleInitialize}
        onCancel={() => setIsInitModalVisible(false)}
        okText="Crear Calendario"
        cancelText="Cancelar"
        width={600}
      >
        <div style={modalTextMargin}>
          <Text>
            Vas a crear un calendario personalizado de {activeLevelTab.label}{" "}
            para esta escuela copiando todas las fechas del calendario general
            de {activeLevelTab.label} de Emmi.
          </Text>
        </div>
        <div style={modalTextMargin}>
          <Text>
            Una vez creado, podrás personalizar las fechas específicamente para
            esta escuela de forma independiente.
          </Text>
        </div>
        <div style={modalSuccessStyle}>
          <Text type="success" strong>
            ✅ El calendario se creará automáticamente con las mismas fechas del
            calendario general de {activeLevelTab.label}.
          </Text>
        </div>
      </Modal>

      {/* Modal para sincronizar con calendario general */}
      <Modal
        title="Sincronizar con Calendario General"
        open={showSyncModal}
        onOk={handleSyncWithGeneral}
        onCancel={() => setShowSyncModal(false)}
        okText="Sí, sincronizar"
        cancelText="Cancelar"
        okType="primary"
        width={600}
      >
        <div style={modalTextMargin}>
          <Text>
            {isDividedByClassrooms && activeClassroom ? (
              <>
                ¿Estás seguro de que quieres sincronizar el calendario de{" "}
                <strong>
                  {
                    availableClassrooms.find((c) => c.id === activeClassroom)
                      ?.name
                  }
                </strong>{" "}
                con el calendario general de Emmi?
              </>
            ) : (
              <>
                ¿Estás seguro de que quieres sincronizar el calendario
                personalizado de {activeLevelTab.label} de esta escuela con el
                calendario general de {activeLevelTab.label} de Emmi?
              </>
            )}
          </Text>
        </div>

        <div style={{ ...modalWarningStyle, marginBottom: 16 }}>
          <Text type="warning" strong>
            ⚠️ Esta acción copiará todas las fechas del calendario general al{" "}
            {isDividedByClassrooms && activeClassroom
              ? `calendario de ${
                  availableClassrooms.find((c) => c.id === activeClassroom)
                    ?.name
                }`
              : `calendario personalizado de ${activeLevelTab.label} de la escuela`}
            .
          </Text>
          <br />
          <Text type="secondary">
            Los cambios personalizados actuales se perderán y serán reemplazados
            por las fechas del calendario general.
          </Text>
        </div>

        <div style={modalSuccessStyle}>
          <Text>
            <strong>Después de la sincronización:</strong> Podrás seguir
            personalizando las fechas{" "}
            {isDividedByClassrooms && activeClassroom
              ? `específicamente para esta clase`
              : "específicamente para esta escuela"}
            .
          </Text>
        </div>
      </Modal>

      {/* Modal de confirmación para guardar cambios */}
      <Modal
        title="Confirmar Cambios"
        open={showConfirmModal}
        onOk={handleFinalConfirm}
        onCancel={handleCancelConfirm}
        okText="Sí, guardar cambios"
        cancelText="Cancelar"
        okType="primary"
        width={600}
      >
        <div style={modalTextMargin}>
          <Text>
            ¿Estás seguro de que quieres guardar estos cambios en el calendario
            de la escuela?
          </Text>
        </div>

        <div style={modalSuccessStyle}>
          <Text type="success">
            <strong>Los cambios se aplicarán:</strong> Las fechas modificadas se
            guardarán permanentemente en el calendario personalizado de esta
            escuela.
          </Text>
        </div>
      </Modal>

      {/* Modal de consulta para ajuste automático */}
      <Modal
        title="Ajustar fechas automáticamente"
        open={showAdjustModal}
        onCancel={() => handleAdjustModalResponse(false)}
        footer={[
          <Button key="no" onClick={() => handleAdjustModalResponse(false)}>
            No, solo esta semana
          </Button>,
          <Button
            key="yes"
            type="primary"
            onClick={() => handleAdjustModalResponse(true)}
          >
            Sí, ajustar automáticamente
          </Button>,
        ]}
        width={600}
      >
        <div style={modalTextMargin}>
          <Text>
            El cambio de fecha que estás realizando afecta la secuencia de
            fechas semanales.
          </Text>
        </div>

        <div style={modalTextMargin}>
          <Text>
            <strong>
              ¿Quieres que las semanas posteriores se ajusten automáticamente?
            </strong>
          </Text>
        </div>

        <div style={{ ...modalSuccessStyle, marginBottom: 16 }}>
          <Text type="success">
            <strong>Sí, ajustar automáticamente:</strong> Las semanas
            posteriores se actualizarán sumando 7 días consecutivos desde la
            nueva fecha.
          </Text>
        </div>

        <div style={modalWarningStyle}>
          <Text type="warning">
            <strong>No, solo esta semana:</strong> Solo se cambiará la fecha de
            esta semana, las demás permanecerán igual.
          </Text>
        </div>
      </Modal>

      {/* Modal de confirmación para unificar calendarios */}
      <Modal
        title="Unificar calendarios"
        open={showUnifyModal}
        onCancel={handleCancelUnify}
        footer={[
          <Button key="cancel" onClick={handleCancelUnify}>
            Conservar por clase
          </Button>,
          <Button
            key="unify"
            type="primary"
            danger
            onClick={handleConfirmUnify}
          >
            Sí, unificar
          </Button>,
        ]}
        width={600}
      >
        <div style={modalTextMargin}>
          <Text>¿Estás seguro de que quieres unificar el calendario?</Text>
        </div>

        <div style={modalTextMargin}>
          <Text>Esta acción eliminará los calendarios por clase.</Text>
        </div>

        <div style={{ ...modalWarningStyle, marginBottom: 16 }}>
          <Text type="warning" strong>
            ⚠️ Los calendarios personalizados por clase se perderán
            permanentemente.
          </Text>
          <br />
          <Text type="secondary">
            Volverás a tener un único calendario para toda la escuela.
          </Text>
        </div>
      </Modal>

      {/* Modal de confirmación para cambio de clase con cambios pendientes */}
      <Modal
        title="Cambios pendientes"
        open={showChangeClassroomModal}
        onCancel={handleCancelClassroomChange}
        footer={[
          <Button key="stay" onClick={handleCancelClassroomChange}>
            Quedarme aquí
          </Button>,
          <Button
            key="discard"
            type="primary"
            danger
            onClick={handleConfirmClassroomChange}
          >
            Descartar cambios y cambiar
          </Button>,
        ]}
        width={600}
      >
        <div style={modalTextMargin}>
          <Text>
            Tienes cambios pendientes sin guardar en el calendario de{" "}
            <strong>
              {activeClassroom
                ? availableClassrooms.find((c) => c.id === activeClassroom)
                    ?.name
                : "esta clase"}
            </strong>
            .
          </Text>
        </div>

        <div style={modalTextMargin}>
          <Text>¿Qué quieres hacer?</Text>
        </div>

        <div style={{ ...modalWarningStyle, marginBottom: 16 }}>
          <Text type="warning" strong>
            ⚠️ Si cambias de clase, los cambios pendientes se perderán.
          </Text>
          <br />
          <Text type="secondary">
            Puedes guardar los cambios actuales usando el botón GUARDAR antes de
            cambiar de clase.
          </Text>
        </div>

        <div style={modalSuccessStyle}>
          <Text>
            <strong>Recomendación:</strong> Guarda los cambios actuales y luego
            cambia de asignatura para no perder tu trabajo.
          </Text>
        </div>
      </Modal>

      {/* Modal de confirmación para cambio de sección con cambios pendientes */}
      <Modal
        title="Cambios pendientes"
        open={showChangeLevelModal}
        onCancel={handleCancelLevelChange}
        footer={[
          <Button key="stay" onClick={handleCancelLevelChange}>
            Quedarme aquí
          </Button>,
          <Button
            key="discard"
            type="primary"
            danger
            onClick={handleConfirmLevelChange}
          >
            Descartar cambios y cambiar
          </Button>,
        ]}
        width={600}
      >
        <div style={modalTextMargin}>
          <Text>
            Tienes cambios pendientes sin guardar en el calendario de{" "}
            <strong>{activeLevelTab.label}</strong>.
          </Text>
        </div>

        <div style={{ ...modalWarningStyle, marginBottom: 16 }}>
          <Text type="warning" strong>
            ⚠️ Si cambias de sección, los cambios pendientes se perderán.
          </Text>
          <br />
          <Text type="secondary">
            Puedes guardar los cambios actuales usando el botón GUARDAR antes de
            cambiar de sección.
          </Text>
        </div>
      </Modal>

      {/* Modal de confirmación para volver al calendario del grupo (destructivo) */}
      <Modal
        title="Volver al calendario del grupo"
        open={showRevertOverrideModal}
        onCancel={() => setShowRevertOverrideModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowRevertOverrideModal(false)}>
            Cancelar
          </Button>,
          <Button key="revert" type="primary" danger onClick={handleRevertOverride}>
            Sí, volver al grupo
          </Button>,
        ]}
        width={600}
      >
        <div style={modalTextMargin}>
          <Text>
            Vas a revertir el calendario de esta asignatura al calendario del
            grupo.
          </Text>
        </div>
        <div style={{ ...modalWarningStyle, marginBottom: 16 }}>
          <Text type="warning" strong>
            ⚠️ El calendario propio de esta asignatura se eliminará
            permanentemente.
          </Text>
        </div>
      </Modal>
    </div>
  );
};

export default CalendarTab;
